import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Bot,
  Check,
  FlaskConical,
  MessageSquare,
  Settings as SettingsIcon,
  Users,
  Users2,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { TOTAL_STEPS, completedStepCount, deriveBotStatus } from '@/lib/botStatus';
import { cn } from '@/lib/utils';

interface SidebarProps {
  /** F067 stretch: called when a nav item is clicked so the mobile Sheet can
   * close. Optional so the desktop `<aside>` usage is unaffected. */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps = {}) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { state, loading } = useOnboardingState();

  // F067: do not derive status while context is still loading (or on fetch
  // error). Render a neutral label with no counter/check until data arrives.
  const isHydrating = loading || state === null;

  const botStatus = isHydrating ? null : deriveBotStatus(state);
  const completed = isHydrating ? 0 : completedStepCount(state);

  const botLabel = isHydrating
    ? t('nav.bot.label_neutral')
    : botStatus === 'live'
      ? t('nav.bot.label_live')
      : t('nav.bot.label_setup');

  return (
    <nav className="flex flex-col gap-1 p-4" aria-label="Main navigation">
      {/* Single bot home entry — points to / (index route) */}
      <NavLink
        to="/"
        end
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            'hover:bg-muted',
            'min-h-11',
            isActive && 'bg-muted font-medium'
          )
        }
      >
        <Bot className="h-4 w-4 shrink-0" />
        <span className="truncate">{botLabel}</span>
        {/* F067: while loading show nothing; after hydration show check or counter */}
        {!isHydrating &&
          (botStatus === 'live' ? (
            <Check
              className="h-4 w-4 text-success ml-auto shrink-0"
              data-testid="sidebar-bot-check"
              aria-hidden="true"
            />
          ) : (
            <span
              className="ml-auto text-xs font-medium text-muted-foreground"
              data-testid="sidebar-bot-count"
            >
              {completed}/{TOTAL_STEPS}
            </span>
          ))}
      </NavLink>

      {/* "Testar o bot" — between Bot home and Leads; no status chip */}
      <NavLink
        to="/test"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            'hover:bg-muted',
            'min-h-11',
            isActive && 'bg-muted font-medium'
          )
        }
      >
        <FlaskConical className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('nav.test')}</span>
      </NavLink>

      <NavLink
        to="/leads"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            'hover:bg-muted',
            'min-h-11',
            isActive && 'bg-muted font-medium'
          )
        }
      >
        <Users className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('nav.leads')}</span>
      </NavLink>

      <NavLink
        to="/conversations"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            'hover:bg-muted',
            'min-h-11',
            isActive && 'bg-muted font-medium'
          )
        }
      >
        <MessageSquare className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('nav.conversations')}</span>
      </NavLink>

      <NavLink
        to="/notifications"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            'hover:bg-muted',
            'min-h-11',
            isActive && 'bg-muted font-medium'
          )
        }
      >
        <Bell className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('nav.notifications')}</span>
      </NavLink>

      <NavLink
        to="/settings"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
            'hover:bg-muted',
            'min-h-11',
            isActive && 'bg-muted font-medium'
          )
        }
      >
        <SettingsIcon className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('nav.settings')}</span>
      </NavLink>

      {isAdmin && (
        <NavLink
          to="/team"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
              'hover:bg-muted',
              'min-h-11',
              isActive && 'bg-muted font-medium'
            )
          }
        >
          <Users2 className="h-4 w-4 shrink-0" />
          <span className="truncate">{t('nav.team')}</span>
        </NavLink>
      )}
    </nav>
  );
}
